import { lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquarePlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalDescription, ModalHeader, ModalTitle, ModalTrigger } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/loader'
import { useCustomerFeedback } from '@/hooks/useCustomerFeedback'

const FeedbackSheet = lazy(() => import('./feedback-sheet').then((m) => ({ default: m.FeedbackSheet })))

/**
 * «Tilbakemelding» for the enterprise customers EventRipple switched on (03.10.2026).
 *
 * Sits beside `HelpLauncher` in `AccountControls`, so it inherits that component's rule:
 * exactly one instance at every width (labelled row in the rail at lg+, 44px icon in the
 * mobile bar). Not a floating button: the bottom-right corner of a phone already holds
 * the create FAB on Arrangementer and the toasts. Renders nothing for everyone else, so
 * no other account's chrome changes. Spec: design-system-v2/02-components/organisms/feedback-sheet.html
 */
export function FeedbackLauncher({ expanded = false }: { expanded?: boolean }) {
  const { t } = useTranslation()
  const { enabled, orgId, orgName } = useCustomerFeedback()
  const [open, setOpen] = useState(false)
  if (!enabled || !orgId) return null
  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>
        <Button
          variant="quiet"
          size={expanded ? 'row' : 'icon'}
          aria-label={t('customerFeedback.open')}
          title={t('customerFeedback.open')}
        >
          <MessageSquarePlus className="size-5" aria-hidden="true" />
          {expanded && <span className="text-label">{t('customerFeedback.open')}</span>}
        </Button>
      </ModalTrigger>
      <ModalContent size="md">
        <ModalHeader>
          <div className="space-y-1">
            <ModalTitle>{t('customerFeedback.title')}</ModalTitle>
            <ModalDescription>{t('customerFeedback.description', { org: orgName })}</ModalDescription>
          </div>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          {open && (
            <Suspense fallback={<Skeleton className="h-64 w-full" />}>
              <FeedbackSheet orgId={orgId} />
            </Suspense>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
